import { useState, useEffect, useCallback } from 'react';
import {
  fetchAdminRoles,
  fetchAdminUsers,
  createAdminRole,
  updateAdminRole,
  deleteAdminRole,
  createAdminUser,
  updateAdminUser,
  toggleAdminUserStatus,
  deleteAdminUser,
  type AdminRole,
  type AdminUser,
} from '@/lib/supabase';

const ALL_PERMISSIONS: { key: string; label: string; icon: string; color: string }[] = [
  { key: 'active_orders', label: 'Active Orders', icon: 'ri-flashlight-line', color: 'bg-amber-100 text-amber-700' },
  { key: 'history', label: 'Order History', icon: 'ri-history-line', color: 'bg-gray-100 text-gray-700' },
  { key: 'products', label: 'Products', icon: 'ri-shopping-bag-line', color: 'bg-orange-100 text-orange-700' },
  { key: 'analytics', label: 'Analytics', icon: 'ri-bar-chart-2-line', color: 'bg-green-100 text-green-700' },
  { key: 'delivery', label: 'Delivery Boys', icon: 'ri-e-bike-2-line', color: 'bg-slate-100 text-slate-700' },
  { key: 'deliverystats', label: 'Delivery Stats', icon: 'ri-bar-chart-box-line', color: 'bg-teal-100 text-teal-700' },
  { key: 'livemap', label: 'Live Map', icon: 'ri-map-2-line', color: 'bg-emerald-100 text-emerald-700' },
  { key: 'visitors', label: 'Area Visitors', icon: 'ri-map-pin-line', color: 'bg-orange-100 text-orange-600' },
  { key: 'customers', label: 'Customers', icon: 'ri-team-line', color: 'bg-cyan-100 text-cyan-700' },
  { key: 'giftcodes', label: 'Gift Codes', icon: 'ri-coupon-3-line', color: 'bg-pink-100 text-pink-700' },
  { key: 'complaints', label: 'Complaints', icon: 'ri-customer-service-2-line', color: 'bg-red-100 text-red-700' },
  { key: 'activity', label: 'Visitor Activity', icon: 'ri-eye-line', color: 'bg-indigo-100 text-indigo-700' },
  { key: 'videopop', label: 'Video Popup', icon: 'ri-video-line', color: 'bg-rose-100 text-rose-700' },
  { key: 'settings', label: 'Settings', icon: 'ri-settings-3-line', color: 'bg-slate-100 text-slate-600' },
  { key: 'roles', label: 'Role Management', icon: 'ri-shield-user-line', color: 'bg-violet-100 text-violet-700' },
];

const ROLE_COLORS = [
  '#6366f1', '#f97316', '#10b981', '#3b82f6', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f59e0b', '#ef4444', '#06b6d4',
];

interface RoleFormState {
  name: string;
  description: string;
  permissions: string[];
  color: string;
}

interface UserFormState {
  name: string;
  phone: string;
  email: string;
  role_id: string;
  password: string;
}

const defaultRoleForm: RoleFormState = {
  name: '', description: '', permissions: [], color: '#6366f1',
};

const defaultUserForm: UserFormState = {
  name: '', phone: '', email: '', role_id: '', password: '',
};

export default function RoleManagementPanel() {
  const [tab, setTab] = useState<'roles' | 'users'>('roles');
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  // Role form modal
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<AdminRole | null>(null);
  const [roleForm, setRoleForm] = useState<RoleFormState>(defaultRoleForm);
  const [roleSaving, setRoleSaving] = useState(false);

  // User form modal
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [userForm, setUserForm] = useState<UserFormState>(defaultUserForm);
  const [userSaving, setUserSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Delete confirm
  const [deleteRoleId, setDeleteRoleId] = useState<string | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((text: string, type: 'success' | 'error' = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [r, u] = await Promise.all([fetchAdminRoles(), fetchAdminUsers()]);
    setRoles(r);
    setUsers(u);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Role Modal ──────────────────────────────────────────────────────────
  const openAddRole = () => {
    setEditingRole(null);
    setRoleForm(defaultRoleForm);
    setShowRoleModal(true);
  };

  const openEditRole = (role: AdminRole) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description ?? '',
      permissions: [...role.permissions],
      color: role.color ?? '#6366f1',
    });
    setShowRoleModal(true);
  };

  const togglePermission = (key: string) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) { showToast('Role name zaroori hai!', 'error'); return; }
    if (roleForm.permissions.length === 0) { showToast('Kam se kam ek permission chahiye!', 'error'); return; }
    setRoleSaving(true);
    let result;
    if (editingRole) {
      result = await updateAdminRole(editingRole.id, roleForm);
    } else {
      result = await createAdminRole(roleForm);
    }
    setRoleSaving(false);
    if (result.success) {
      showToast(editingRole ? 'Role update ho gaya!' : 'Naya role create ho gaya!');
      setShowRoleModal(false);
      loadData();
    } else {
      showToast(result.error ?? 'Kuch galat ho gaya', 'error');
    }
  };

  const handleDeleteRole = async (id: string) => {
    const result = await deleteAdminRole(id);
    if (result.success) {
      showToast('Role delete ho gaya!');
      setDeleteRoleId(null);
      loadData();
    } else {
      showToast(result.error ?? 'Delete nahi ho saka', 'error');
    }
  };

  // ── User Modal ──────────────────────────────────────────────────────────
  const openAddUser = () => {
    setEditingUser(null);
    setUserForm(defaultUserForm);
    setShowPassword(false);
    setShowUserModal(true);
  };

  const openEditUser = (user: AdminUser) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      phone: user.phone,
      email: user.email ?? '',
      role_id: user.role_id ?? '',
      password: '',
    });
    setShowPassword(false);
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name.trim()) { showToast('Naam zaroori hai!', 'error'); return; }
    if (!userForm.phone.trim()) { showToast('Phone zaroori hai!', 'error'); return; }
    if (!userForm.role_id) { showToast('Role select karo!', 'error'); return; }
    if (!editingUser && !userForm.password.trim()) { showToast('Password zaroori hai!', 'error'); return; }
    if (userForm.password && userForm.password.length < 4) { showToast('Password kam se kam 4 characters ka hona chahiye!', 'error'); return; }
    const selectedRole = roles.find(r => r.id === userForm.role_id);
    setUserSaving(true);
    const payload: Record<string, unknown> = {
      name: userForm.name,
      phone: userForm.phone,
      email: userForm.email,
      role_id: userForm.role_id,
      role_name: selectedRole?.name ?? '',
    };
    // Only update password if provided
    if (userForm.password.trim()) {
      payload.password = userForm.password.trim();
    }
    let result;
    if (editingUser) {
      result = await updateAdminUser(editingUser.id, payload as Parameters<typeof updateAdminUser>[1]);
    } else {
      result = await createAdminUser(payload as Parameters<typeof createAdminUser>[0]);
    }
    setUserSaving(false);
    if (result.success) {
      showToast(editingUser ? 'User update ho gaya!' : 'Naya staff account create ho gaya!');
      setShowUserModal(false);
      loadData();
    } else {
      showToast(result.error ?? 'Kuch galat ho gaya', 'error');
    }
  };

  const handleToggleUser = async (userId: string, isActive: boolean) => {
    const result = await toggleAdminUserStatus(userId, !isActive);
    if (result.success) {
      showToast(!isActive ? 'User active ho gaya!' : 'User deactivate ho gaya!');
      loadData();
    } else {
      showToast('Status change nahi ho saka', 'error');
    }
  };

  const handleDeleteUser = async (id: string) => {
    const result = await deleteAdminUser(id);
    if (result.success) {
      showToast('User delete ho gaya!');
      setDeleteUserId(null);
      loadData();
    } else {
      showToast(result.error ?? 'Delete nahi ho saka', 'error');
    }
  };

  const getRoleById = (id?: string | null) => roles.find(r => r.id === id);

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3.5 rounded-2xl text-white text-sm font-semibold flex items-center gap-3 whitespace-nowrap shadow-2xl ${
          toast.type === 'success' ? 'bg-emerald-500' : 'bg-rose-500'
        }`}>
          <i className={toast.type === 'success' ? 'ri-check-line' : 'ri-close-line'}></i>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div className="bg-slate-900 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shrink-0 shadow-lg">
            <i className="ri-shield-user-line text-xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-lg leading-none">Role Management</h2>
            <p className="text-slate-400 text-xs mt-1">Staff ko roles assign karo aur features ka access control karo</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-center">
              <p className="text-white font-bold text-xl leading-none">{roles.length}</p>
              <p className="text-slate-400 text-[10px]">Roles</p>
            </div>
            <div className="w-px h-8 bg-slate-700"></div>
            <div className="text-center">
              <p className="text-white font-bold text-xl leading-none">{users.filter(u => u.is_active).length}</p>
              <p className="text-slate-400 text-[10px]">Active Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 bg-white rounded-2xl border border-gray-100 p-1.5">
        <button
          onClick={() => setTab('roles')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
            tab === 'roles' ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-shield-star-line"></i>
          Roles ({roles.length})
        </button>
        <button
          onClick={() => setTab('users')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
            tab === 'users' ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-team-line"></i>
          Staff Users ({users.length})
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-slate-900 animate-spin"></div>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      ) : (
        <>
          {/* ── ROLES TAB ── */}
          {tab === 'roles' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-700 font-semibold text-sm">{roles.length} roles defined</p>
                <button
                  onClick={openAddRole}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Naya Role
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {roles.map(role => (
                  <div key={role.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-gray-200 transition-all">
                    <div className="flex items-start gap-4">
                      {/* Role color dot */}
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
                        style={{ backgroundColor: role.color + '20' }}
                      >
                        <i className="ri-shield-star-line text-lg" style={{ color: role.color }}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-gray-900 text-sm">{role.name}</h3>
                          <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: role.color + '20', color: role.color }}
                          >
                            {role.permissions.length} permissions
                          </span>
                        </div>
                        {role.description && (
                          <p className="text-gray-400 text-xs mt-0.5">{role.description}</p>
                        )}
                        {/* Permissions chips */}
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {role.permissions.map(perm => {
                            const p = ALL_PERMISSIONS.find(ap => ap.key === perm);
                            if (!p) return null;
                            return (
                              <span key={perm} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold ${p.color}`}>
                                <i className={`${p.icon} text-xs`}></i>
                                {p.label}
                              </span>
                            );
                          })}
                        </div>
                        {/* Users with this role */}
                        {(() => {
                          const roleUsers = users.filter(u => u.role_id === role.id);
                          if (roleUsers.length === 0) return null;
                          return (
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50">
                              <i className="ri-team-line text-gray-400 text-xs"></i>
                              <span className="text-gray-500 text-xs">{roleUsers.length} staff assigned:</span>
                              <div className="flex gap-1 flex-wrap">
                                {roleUsers.slice(0, 3).map(u => (
                                  <span key={u.id} className="text-xs text-gray-600 font-medium bg-gray-100 px-2 py-0.5 rounded-full">{u.name}</span>
                                ))}
                                {roleUsers.length > 3 && (
                                  <span className="text-xs text-gray-400">+{roleUsers.length - 3} more</span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openEditRole(role)}
                          className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all cursor-pointer"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteRoleId(role.id)}
                          className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-all cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── USERS TAB ── */}
          {tab === 'users' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-700 font-semibold text-sm">{users.length} staff members</p>
                <button
                  onClick={openAddUser}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-user-add-line"></i>
                  Staff Add Karo
                </button>
              </div>

              {users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100 gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                    <i className="ri-team-line text-3xl text-gray-400"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-700 font-semibold">Koi staff user nahi</p>
                    <p className="text-gray-400 text-sm mt-1">Staff add karo aur unhe roles assign karo</p>
                  </div>
                  <button
                    onClick={openAddUser}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-user-add-line"></i>
                    Pehla Staff Add Karo
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {users.map(user => {
                    const role = getRoleById(user.role_id);
                    return (
                      <div key={user.id} className={`bg-white rounded-2xl border p-4 transition-all ${user.is_active ? 'border-gray-100' : 'border-gray-100 opacity-60'}`}>
                        <div className="flex items-center gap-4">
                          {/* Avatar */}
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 font-bold text-white text-sm"
                            style={{ backgroundColor: role?.color ?? '#6b7280' }}>
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold text-gray-900 text-sm">{user.name}</p>
                              {!user.is_active && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>
                              )}
                              {role && (
                                <span
                                  className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: (role.color ?? '#6366f1') + '20', color: role.color ?? '#6366f1' }}
                                >
                                  {role.name}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap mt-0.5">
                              <p className="text-gray-500 text-xs flex items-center gap-1">
                                <i className="ri-phone-line text-gray-400"></i>
                                {user.phone}
                              </p>
                              {user.email && (
                                <p className="text-gray-400 text-xs flex items-center gap-1">
                                  <i className="ri-mail-line text-gray-400"></i>
                                  {user.email}
                                </p>
                              )}
                            </div>
                            {/* Password indicator */}
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                <i className="ri-lock-password-line text-xs"></i>
                                Password set hai
                              </span>
                            </div>
                            {user.last_login_at && (
                              <p className="text-gray-400 text-[10px] mt-0.5 flex items-center gap-1">
                                <i className="ri-time-line"></i>
                                Last login: {new Date(user.last_login_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                            {role && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {role.permissions.slice(0, 4).map(perm => {
                                  const p = ALL_PERMISSIONS.find(ap => ap.key === perm);
                                  if (!p) return null;
                                  return (
                                    <span key={perm} className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${p.color}`}>
                                      <i className={`${p.icon} text-xs`}></i>
                                      {p.label}
                                    </span>
                                  );
                                })}
                                {role.permissions.length > 4 && (
                                  <span className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-gray-100 text-gray-500">
                                    +{role.permissions.length - 4} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {/* Actions */}
                          <div className="flex flex-col gap-1.5 shrink-0">
                            <button
                              onClick={() => openEditUser(user)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all cursor-pointer"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleToggleUser(user.id, user.is_active)}
                              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                                user.is_active ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'bg-green-50 text-green-600 hover:bg-green-100'
                              }`}
                            >
                              <i className={user.is_active ? 'ri-pause-circle-line text-sm' : 'ri-play-circle-line text-sm'}></i>
                            </button>
                            <button
                              onClick={() => setDeleteUserId(user.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-all cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── ROLE MODAL ── */}
      {showRoleModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl max-h-[92vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                  <i className="ri-shield-star-line text-violet-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base">{editingRole ? 'Role Edit Karo' : 'Naya Role Banao'}</h3>
                  <p className="text-gray-400 text-xs">Features ka access define karo</p>
                </div>
              </div>
              <button onClick={() => setShowRoleModal(false)} className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {/* Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Role Name *</label>
                <input
                  type="text"
                  value={roleForm.name}
                  onChange={e => setRoleForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Order Manager, Accounts Staff..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Description</label>
                <input
                  type="text"
                  value={roleForm.description}
                  onChange={e => setRoleForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Yeh role kya kaam karta hai..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
                />
              </div>

              {/* Color */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Role Color</label>
                <div className="flex gap-2 flex-wrap">
                  {ROLE_COLORS.map(color => (
                    <button
                      key={color}
                      onClick={() => setRoleForm(p => ({ ...p, color }))}
                      className={`w-8 h-8 rounded-full cursor-pointer transition-all border-2 ${roleForm.color === color ? 'border-gray-900 scale-110' : 'border-transparent hover:scale-105'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Permissions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                    Permissions ({roleForm.permissions.length}/{ALL_PERMISSIONS.length})
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setRoleForm(p => ({ ...p, permissions: ALL_PERMISSIONS.map(ap => ap.key) }))}
                      className="text-xs text-violet-600 font-semibold cursor-pointer hover:text-violet-800 whitespace-nowrap"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setRoleForm(p => ({ ...p, permissions: [] }))}
                      className="text-xs text-gray-500 font-semibold cursor-pointer hover:text-gray-700 whitespace-nowrap"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_PERMISSIONS.map(perm => {
                    const selected = roleForm.permissions.includes(perm.key);
                    return (
                      <button
                        key={perm.key}
                        onClick={() => togglePermission(perm.key)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer text-left border-2 ${
                          selected
                            ? 'border-slate-900 bg-slate-900 text-white'
                            : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${selected ? 'bg-white/20' : 'bg-white'}`}>
                          <i className={`${perm.icon} text-xs ${selected ? 'text-white' : 'text-gray-500'}`}></i>
                        </div>
                        <span className="leading-tight">{perm.label}</span>
                        {selected && <i className="ri-check-line ml-auto shrink-0 text-xs"></i>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex gap-3">
              <button
                onClick={() => setShowRoleModal(false)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm cursor-pointer hover:bg-gray-200 transition-all whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRole}
                disabled={roleSaving}
                className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-bold text-sm cursor-pointer hover:bg-slate-800 transition-all flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap"
              >
                {roleSaving ? <><i className="ri-loader-4-line animate-spin"></i>Saving...</> : <><i className="ri-save-line"></i>Save Role</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── USER MODAL ── */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl max-h-[92vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                  <i className="ri-user-add-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base">{editingUser ? 'Staff Edit Karo' : 'Staff Add Karo'}</h3>
                  <p className="text-gray-400 text-xs">Phone + Password se login karega</p>
                </div>
              </div>
              <button onClick={() => setShowUserModal(false)} className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Staff ka Naam *</label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={e => setUserForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Full name"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 outline-none"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center gap-1">
                  <i className="ri-phone-line text-gray-500"></i>
                  Phone Number * (Login ID)
                </label>
                <input
                  type="tel"
                  value={userForm.phone}
                  onChange={e => setUserForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+91 98765 43210"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 outline-none"
                />
                <p className="text-gray-400 text-[11px] mt-1 flex items-center gap-1">
                  <i className="ri-information-line text-gray-400"></i>
                  Yeh phone number hi login ke waqt ID ki tarah use hoga
                </p>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center gap-1">
                  <i className="ri-lock-password-line text-gray-500"></i>
                  Password {editingUser ? '(khali chhodo agar change na karna ho)' : '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={userForm.password}
                    onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))}
                    placeholder={editingUser ? 'Naya password (optional)' : 'Strong password likho...'}
                    className="w-full pl-4 pr-12 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
                {userForm.password && (
                  <div className="flex gap-1.5 mt-1.5">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          userForm.password.length > i * 2
                            ? userForm.password.length >= 8 ? 'bg-green-500' : userForm.password.length >= 6 ? 'bg-amber-400' : 'bg-red-400'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                    <span className={`text-[10px] font-semibold ml-1 whitespace-nowrap ${
                      userForm.password.length >= 8 ? 'text-green-600' : userForm.password.length >= 6 ? 'text-amber-500' : 'text-red-500'
                    }`}>
                      {userForm.password.length >= 8 ? 'Strong' : userForm.password.length >= 6 ? 'Medium' : 'Weak'}
                    </span>
                  </div>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Email (Optional)</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="staff@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 outline-none"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Role Assign Karo *</label>
                {roles.length === 0 ? (
                  <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs">
                    Pehle &ldquo;Roles&rdquo; tab mein jaake ek role banao!
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {roles.map(role => (
                      <button
                        key={role.id}
                        onClick={() => setUserForm(p => ({ ...p, role_id: role.id }))}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer border-2 text-left ${
                          userForm.role_id === role.id
                            ? 'border-slate-900 bg-slate-900 text-white'
                            : 'border-gray-100 bg-gray-50 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                          style={{ backgroundColor: userForm.role_id === role.id ? 'rgba(255,255,255,0.2)' : (role.color + '20') }}
                        >
                          <i className="ri-shield-star-line text-sm" style={{ color: userForm.role_id === role.id ? 'white' : role.color }}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="leading-none">{role.name}</p>
                          {role.description && <p className={`text-[10px] mt-0.5 ${userForm.role_id === role.id ? 'text-white/70' : 'text-gray-400'}`}>{role.description}</p>}
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${userForm.role_id === role.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}`}>
                          {role.permissions.length} features
                        </span>
                        {userForm.role_id === role.id && <i className="ri-check-line shrink-0"></i>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Login credentials preview */}
              {(userForm.phone || userForm.password) && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <p className="text-slate-700 text-xs font-bold mb-2 flex items-center gap-1">
                    <i className="ri-key-2-line text-slate-500"></i>
                    Login Credentials Preview
                  </p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-500 w-16 shrink-0">Phone ID:</span>
                      <span className="text-xs font-mono text-slate-700 bg-white px-2 py-0.5 rounded border border-slate-200">{userForm.phone || '—'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-500 w-16 shrink-0">Password:</span>
                      <span className="text-xs font-mono text-slate-700 bg-white px-2 py-0.5 rounded border border-slate-200">
                        {userForm.password ? '•'.repeat(userForm.password.length) : '—'}
                      </span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-[10px] mt-2">Yeh credentials staff ko share karo</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex gap-3">
              <button
                onClick={() => setShowUserModal(false)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm cursor-pointer hover:bg-gray-200 transition-all whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                disabled={userSaving}
                className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-bold text-sm cursor-pointer hover:bg-slate-800 transition-all flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap"
              >
                {userSaving ? <><i className="ri-loader-4-line animate-spin"></i>Saving...</> : <><i className="ri-save-line"></i>{editingUser ? 'Update Karo' : 'Account Banao'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE ROLE CONFIRM ── */}
      {deleteRoleId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl">
            <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-3xl text-red-500"></i>
            </div>
            <h3 className="text-gray-900 font-bold text-base text-center mb-1">Role Delete Karo?</h3>
            <p className="text-gray-500 text-sm text-center mb-5">Yeh role delete ho jaayega. Is role ke users unassigned ho jaayenge.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteRoleId(null)} className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => handleDeleteRole(deleteRoleId)} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm cursor-pointer whitespace-nowrap">Delete Karo</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE USER CONFIRM ── */}
      {deleteUserId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl">
            <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-unfollow-line text-3xl text-red-500"></i>
            </div>
            <h3 className="text-gray-900 font-bold text-base text-center mb-1">Staff Delete Karo?</h3>
            <p className="text-gray-500 text-sm text-center mb-5">Is staff member ko permanently delete kar doge?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteUserId(null)} className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => handleDeleteUser(deleteUserId)} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm cursor-pointer whitespace-nowrap">Delete Karo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
